import { DcmJsDatasetWrapper } from '../dicom/dcmJsWrapper';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import {
    DicomStructureSet,
    RoiInterpretedType,
} from '../dicom/dicomstructureset';
import { DicomRoi } from '../dicom/dicomRoi';
import { Modality } from '../dicom/modality';
import {
    DicomProcessor,
    dcmjsRemoveStructure,
} from '../services/dicomProcessor';
import { ChangeTracker } from '../changes/ChangeTracker';
import {
    StructureSetSnapshot,
    SyncStatus,
    TrackableObject,
} from '../changes/DomainChange';
import { newStructureSetId, StructureSetId } from '../changes/ids';
import { ApprovedStructureError } from '../errors/DomainError';
import { Roi } from './roi';
import { Image3D } from './image3d';

/**
 * Patient-level structure set domain model that wraps DicomStructureSet.
 *
 * Provides a higher-level interface for structure set operations, combining
 * DICOM-level functionality with patient-specific concerns like image association.
 * Maintains immutability through the withX() pattern for all state changes.
 */
export class StructureSet implements Iterable<DicomRoi>, TrackableObject {
    readonly trackableKind = 'structureSet' as const;
    readonly id: StructureSetId;
    private _roiObjects: Roi[] = [];
    private tracker: ChangeTracker | null = null;
    private _syncStatus: SyncStatus = 'downloaded';

    public frameOfReferenceUid: string;

    public get structureSetLabel(): string {
        return this.dicomStructureSet.structureSetLabel;
    }

    public get structureSetDescription(): string {
        return this.dicomStructureSet.structureSetDescription;
    }

    public get approvalStatus(): DICOMApprovalStatus {
        return this.dicomStructureSet.approvalStatus;
    }

    public get isApproved(): boolean {
        return this.approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    public get reviewerName(): string {
        return this.dicomStructureSet.reviewerName;
    }

    public get reviewTime(): string {
        return this.dicomStructureSet.reviewTime;
    }

    public get reviewDate(): string {
        return this.dicomStructureSet.reviewDate;
    }

    public get dicomUid(): string {
        return this.dicomStructureSet.sopInstanceUid;
    }

    public get seriesInstanceUid(): string {
        return this.dicomStructureSet.seriesInstanceUid;
    }

    public get manufacturer(): string {
        return this.dicomStructureSet.manufacturer;
    }

    public get manufacturerModelName(): string {
        return this.dicomStructureSet.manufacturerModelName;
    }

    public get softwareVersions(): string {
        return this.dicomStructureSet.softwareVersions;
    }

    get trackableId(): StructureSetId {
        return this.id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
        for (const r of this._roiObjects) {
            r.attachTracker(tracker);
        }
    }

    /** New Roi[] collection — the domain-level wrappers. */
    get rois(): readonly Roi[] {
        return this._roiObjects;
    }

    /**
     * Creates a new ROI in the structure set, wraps it in a domain
     * {@link Roi}, and appends it to the {@link rois} collection so the
     * Roi[] wrapper array stays consistent with the underlying
     * DicomStructureSet. The new Roi inherits this SS's tracker.
     *
     * Does NOT emit a changeset — creation tracking is the caller's
     * concern (services wrap this in a command() and markChanged).
     */
    createRoi(
        interpretedType?: RoiInterpretedType,
        structureName: string = ''
    ): Roi {
        const dicomRoi = this.dicomStructureSet.createRoi(
            interpretedType,
            structureName
        );
        const roi = new Roi(dicomRoi);
        if (this.tracker) {
            roi.attachTracker(this.tracker);
        }
        this._roiObjects = [...this._roiObjects, roi];
        return roi;
    }

    /**
     * Deletes the ROI with the given roiNumber from both the underlying
     * DicomStructureSet and the {@link rois} wrapper collection, keeping
     * the two consistent. No-op if no ROI with that number exists.
     *
     * Does NOT emit a changeset — deletion tracking is the caller's
     * concern.
     */
    deleteRoi(roiNumber: number): void {
        this.dicomStructureSet.deleteRoi(roiNumber);
        this._roiObjects = this._roiObjects.filter(
            (r) => r.roiNumber !== roiNumber
        );
    }

    /**
     * Internal/test seam: replaces the Roi[] wrapper collection wholesale.
     * Production code should rely on the constructor wrap plus
     * {@link syncRoiObjects} instead.
     */
    setRoiObjects(rois: Roi[]): void {
        this._roiObjects = rois;
    }

    /**
     * Reconciles the domain {@link rois} wrapper collection with the
     * underlying DicomStructureSet after dicom-level mutations (e.g.
     * structure-set copy, which clones/creates/deletes DicomRois without
     * going through {@link createRoi}/{@link deleteRoi}).
     *
     * Existing wrappers are kept (preserving RoiId identity), wrappers
     * whose DicomRoi no longer exists are dropped, and unwrapped
     * DicomRois get fresh wrappers that inherit this structure set's
     * tracker. Idempotent.
     */
    syncRoiObjects(): void {
        const wrapperByDicomRoi = new Map<DicomRoi, Roi>();
        for (const roi of this._roiObjects) {
            wrapperByDicomRoi.set(roi.dicomRoi, roi);
        }
        const synced: Roi[] = [];
        for (const dicomRoi of this.dicomStructureSet.rois.values()) {
            const existing = wrapperByDicomRoi.get(dicomRoi);
            if (existing) {
                synced.push(existing);
                continue;
            }
            const roi = new Roi(dicomRoi);
            if (this.tracker) {
                roi.attachTracker(this.tracker);
            }
            synced.push(roi);
        }
        this._roiObjects = synced;
    }

    get syncStatus(): SyncStatus {
        return this._syncStatus;
    }

    setSyncStatus(s: SyncStatus): void {
        this._syncStatus = s;
        this.tracker?.markChanged(this);
    }

    /** New domain method (preferred). Existing setter delegates to this. */
    rename(newName: string): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot rename approved structure set '${this.structureSetLabel}'`
            );
        }
        this.dicomStructureSet.structureSetLabel = newName;
        this.tracker?.markChanged(this);
    }

    setDescription(description: string): void {
        this._ensureCanModify();
        this.dicomStructureSet.structureSetDescription = description;
        this.tracker?.markChanged(this);
    }

    getRoiById(id: StructureSetId | string): Roi | undefined {
        return this._roiObjects.find((r) => r.id === id);
    }

    captureSnapshot(): StructureSetSnapshot {
        return {
            id: this.id,
            displayName: this.structureSetLabel,
            description: this.structureSetDescription,
            approvalStatus: this.approvalStatus,
            reviewerName: this.reviewerName,
            frameOfReferenceUid: this.frameOfReferenceUid,
            sopInstanceUid: this.dicomStructureSet.sopInstanceUid,
            seriesInstanceUid: this.seriesInstanceUid,
            imageSeriesUid: this._image?.seriesUid ?? null,
            roiCount: this._roiObjects.length,
            syncStatus: this._syncStatus,
        };
    }

    /**
     * Map where key is the {@link DicomRoi.roiNumber} and value is the {@link DicomRoi}.
     * @deprecated Prefer the {@link rois} array of domain-level {@link Roi} wrappers.
     */
    public get dicomRois(): ReadonlyMap<number, DicomRoi> {
        return this.dicomStructureSet.rois;
    }

    public get roiArray(): DicomRoi[] {
        return Array.from(this.dicomStructureSet.rois.values());
    }

    public get image(): Image3D | null {
        return this._image;
    }

    public set image(image: Image3D | null) {
        this._image = image;
    }

    public get numberOfStructures(): number {
        return this.dicomStructureSet.numberOfStructures;
    }

    /**
     * Get an ROI by its roiNumber.
     * @param roiNumber - The number of the ROI you want to retrieve.
     * @returns The corresponding ROI, or undefined if not found.
     */
    public getRoiByNumber(roiNumber: number): DicomRoi | undefined {
        return this.dicomStructureSet.rois.get(roiNumber);
    }

    /**
     * Sets approval status with cascading (mutable version).
     * @param approvalStatus The new approval status
     * @param reviewerName The reviewer name
     * @param cascade If true, also updates all ROI approval statuses to match
     */
    public setApprovalStatus(
        approvalStatus: DICOMApprovalStatus,
        reviewerName: string,
        cascade: boolean = true
    ): void {
        this.dicomStructureSet.setApprovalStatus(approvalStatus, reviewerName);
        if (cascade) {
            // Cascade through the tracked Roi wrappers (not the raw DICOM
            // objects) so each per-ROI approval flip is marked and reaches
            // the coalesced changeset. Writing roi.approvalStatus directly
            // bypassed the tracker, so consumers saw only the SS entry and
            // none of the ROIs.
            for (const roi of this._roiObjects) {
                switch (approvalStatus) {
                    case DICOMApprovalStatus.APPROVED:
                        roi.approve(reviewerName);
                        break;
                    case DICOMApprovalStatus.REJECTED:
                        roi.reject(reviewerName);
                        break;
                    default:
                        roi.unapprove();
                        break;
                }
            }
        }
        // Approval transitions ARE persistence-relevant: persistence and
        // the bridge both need to see the new status / reviewer.
        this.tracker?.markChanged(this);
    }

    /**
     * Get all ROIs with a specific approval status.
     * @param approvalStatus - The approval status to filter by.
     * @returns An array of ROIs with the specified approval status.
     *
     */
    public getRoiByApprovalStatus(
        approvalStatus: DICOMApprovalStatus
    ): DicomRoi[] {
        return this.roiArray.filter(
            (roi) => roi.approvalStatus === approvalStatus
        );
    }

    /**
     * Determines if the structure set approval status should cascade based on ROI states.
     *
     * @returns The cascaded approval status, or null if no change needed:
     * - APPROVED if all ROIs are approved
     * - REJECTED if all ROIs are rejected
     * - UNAPPROVED if ROIs have mixed states
     * - null if current status already matches the calculated status
     */
    public getCascadingApprovalStatus(): DICOMApprovalStatus | null {
        if (this.numberOfStructures === 0) {
            return null;
        }

        const approvedCount = this.getRoiByApprovalStatus(
            DICOMApprovalStatus.APPROVED
        ).length;
        const rejectedCount = this.getRoiByApprovalStatus(
            DICOMApprovalStatus.REJECTED
        ).length;

        let targetStatus: DICOMApprovalStatus;

        if (approvedCount === this.numberOfStructures) {
            targetStatus = DICOMApprovalStatus.APPROVED;
        } else if (rejectedCount === this.numberOfStructures) {
            targetStatus = DICOMApprovalStatus.REJECTED;
        } else {
            targetStatus = DICOMApprovalStatus.UNAPPROVED;
        }

        return this.approvalStatus === targetStatus ? null : targetStatus;
    }

    public exportWith(roiNumbers: number[], characterSet?: string): Blob {
        const dicomClone = new DcmJsDatasetWrapper(
            DicomProcessor.cloneDicomDataset(this.dicomStructureSet)
        );
        if (characterSet) {
            dicomClone.setCharacterSet(characterSet);
        }
        const allRois = this.dicomStructureSet.rois.keys();
        for (const roiNumber of allRois) {
            if (!roiNumbers.includes(roiNumber)) {
                dcmjsRemoveStructure(dicomClone, roiNumber);
            }
        }
        return dicomClone.asBlob();
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this.dicomStructureSet.generationModel;
    }

    public get isNew(): boolean {
        return this.dicomStructureSet.isNew;
    }

    public get isExported(): boolean {
        return this.dicomStructureSet.isExported;
    }

    /**
     * Records that this structure set was exported/uploaded. Tracked —
     * the isNew/isExported flags feed version-cached read models (export
     * dialog), which must invalidate on this transition.
     */
    public setExported(): void {
        this.dicomStructureSet.setExported();
        this.tracker?.markChanged(this);
    }

    public export(): Blob {
        return this.dicomStructureSet.datasetWrapper.asBlob();
    }
    /**
     * Exports the structure set as a Monaco DIR file.
     * This is a custom format used for sharing structure sets in a specific way.
     * @param patientName - The name of the patient, used in the export.
     * @returns A Blob representing the exported structure set in Monaco format.
     */
    public exportAsMonacoFile(patientName: string): Blob {
        if (!this.image) {
            throw new Error(
                'Cannot export Monaco file without an associated image.'
            );
        }

        const modality = this.image.seriesMetadata.modality;

        if (modality !== Modality.CT && modality !== Modality.MR) {
            throw new Error(
                'Can only export Monaco file for CT or MR modalities.'
            );
        }
        const formatOrientation = (num: number): string => {
            if (num === 0) {
                return '0';
            }
            if (num === 1) {
                return '1';
            }
            if (num === -1) {
                return '-1';
            }
            return num.toFixed(6);
        };

        const imagePositionLines = Array.from(
            this.image.firstSlice.imagePosition
        ).map((x) => x.toFixed(4));
        // export only the X and Y direction cosines as defined in DICOM
        const imageOrientationLines = Array.from(
            this.image.firstSlice.imageOrientation.slice(0, 6) // Only take the first 6 elements for orientation
        ).map(formatOrientation);

        const monacoDirFileLines: (string | number)[] = [
            '!Version=2',
            modality,
            this.image.sopClassUid,
            this.image.seriesMetadata.seriesInstanceUID,
            this.image.firstSlice.studyInstanceUID,
            'NO_ID',
            patientName,
            this.image.frameOfReferenceUid,
            ...imagePositionLines,
            ...imageOrientationLines,
            'RTSTRUCT',
            '1.2.840.10008.5.1.4.1.1.481.3', // DICOMClassSopUIDs.rt_struct.value
            this.dicomStructureSet.sopInstanceUid,
            this.dicomStructureSet.seriesInstanceUid,
            'NO_ID',
            this.structureSetDescription || 'NO_DESCRIPTION',
            '',
            '',
            '',
            '',
            0,
            0,
            0,
            0,
            0,
            0,
        ];

        const monacoDirFileContent = monacoDirFileLines.join('\n') + '\n';

        return new Blob([monacoDirFileContent], {
            type: 'text/plain;charset=utf-8',
        });
    }

    private _ensureCanModify() {
        if (this.isApproved) {
            throw new Error(
                `Cannot modify approved structure set '${this.structureSetLabel}'`
            );
        }
    }

    constructor(
        public readonly dicomStructureSet: DicomStructureSet,
        frameOfReferenceUid: string,
        private _image: Image3D | null,
        id: StructureSetId = newStructureSetId()
    ) {
        this.frameOfReferenceUid = frameOfReferenceUid;
        this.id = id;
        // Wrap any DicomRois present at construction so the domain rois
        // collection is a constructor-owned invariant rather than an
        // external hydration step. Structure sets created mid-session
        // (copy, import) are therefore consistent from birth; a later
        // attachTracker propagates the tracker to these wrappers.
        this.syncRoiObjects();
    }

    public [Symbol.iterator](): Iterator<DicomRoi> {
        return this.dicomStructureSet[Symbol.iterator]();
    }
}
